import { IRowNode } from 'ag-grid-community';
import { Component, ElementRef, EventEmitter, HostBinding, Input, OnInit, Output, ViewChild } from '@angular/core';
import { NodeUtils } from '@core/utils/node.utils';
import { BaseComponent } from '@portal-shared/app/components/base-components/base.component';
import { Period } from '@portal-shared/app/entities/period.model';
import { TimerUtils } from '@portal-shared/app/utils/timer.utils';
import { DraggingService } from '@services/dragging.service';
import { PlanningRow } from '@shared/components/grids/rows/planning-row';

@Component({
  standalone: true,
  selector: 'al-cell-drop-zone',
  templateUrl: './cell-drop-zone.component.html',
  styleUrl: './cell-drop-zone.component.scss'
})
export class CellDropZoneComponent extends BaseComponent implements OnInit {
  @ViewChild('dropzone')
  public dropzone?: ElementRef<HTMLDivElement>;

  @Input() public droppable = true;
  @Input() public node?: IRowNode<PlanningRow>;
  @Input() public period?: Period;

  @HostBinding('attr.row-id')
  public get rowId() {
    return this.node?.id;
  }

  @HostBinding('attr.data-period-id')
  public get periodId() {
    return this.period?.id;
  }

  @Output()
  public dragOverDelay = new EventEmitter<void>();
  private dragOverTimeout?: ReturnType<typeof setTimeout>;

  public isDragOver = false;
  public isDragOverChild = false;
  public isDragging = false;
  private isMouseOver = false;

  public constructor(private draggingService: DraggingService) {
    super();
  }

  public ngOnInit() {
    this.subscribe(
      this.draggingService.observeIsDragging().subscribe(isDragging => {
        this.isDragging = isDragging;
      })
    );

    this.subscribe(
      this.draggingService.observeDragPayload().subscribe(payload => {
        const { node, period } = payload.draggedOver ?? {};
        const dragOverRowId = node?.id;

        const isSamePeriodCell = !period || this.period?.id === period.id;
        const isFullRow =
          (!!payload.dragged.assignment || !!payload.dragged.resource) && !payload.dragged.assignmentPeriod;

        let isDragOver = (isSamePeriodCell || isFullRow) && this.rowId === dragOverRowId;
        this.isDragOverChild =
          (isSamePeriodCell || isFullRow) &&
          !!dragOverRowId &&
          NodeUtils.getChildren(this.node).some(r => r.data?.rowId === this.rowId);

        if (!isFullRow && period) {
          isDragOver &&= period.id === this.period?.id;
          if (this.period) {
            isDragOver &&= period.equals(this.period);
          }
        }

        if (isDragOver !== this.isDragOver) {
          this.isDragOver = isDragOver;
          this.onIsDragOverChanged();
        }
      })
    );

    this.subscribe(
      this.draggingService.observeDragPosition().subscribe(position => {
        const mouseHover = position.target && this.dropzone?.nativeElement.contains(position.target);
        if (mouseHover && !this.isMouseOver) {
          this.isMouseOver = true;
          this.onDragEnter();
        } else if (!mouseHover && this.isMouseOver) {
          this.isMouseOver = false;
          this.onDragLeave();
        }
      })
    );

    this.subscribe(
      this.draggingService.observeDropPayload().subscribe(() => this.isDragOver && this.dragOverDelay.emit())
    );
  }

  private onDragLeave() {
    this.draggingService.setDragOverPayload({});
  }

  private onIsDragOverChanged() {
    if (this.dragOverTimeout) {
      clearTimeout(this.dragOverTimeout);
    }

    this.dragOverTimeout = setTimeout(() => {
      if (this.isDragOver) {
        this.dragOverDelay.emit();
      }
    }, TimerUtils.humanThresholdMs * 2);
  }

  private onDragEnter() {
    const { node, period } = this;
    this.draggingService.setDragOverPayload({ node, period });
  }
}
